// Pure calculation helper functions for SafeHaven Scorecard
import { scorecardRandom } from "@/dev/scorecardParityTestkit"; // SCORECARD_PARITY_TESTKIT

/**
 * Calculate average and convert to percentage with 1 decimal
 */
export const avgCal = (rowData) => {
  let sum = 0;
  for (let i = 0; i < rowData.length; i++) {
    sum += rowData[i];
  }
  const avg = sum / rowData.length;
  return (avg * 100).toFixed(1);
};

/**
 * Find maximum value and convert to percentage with 2 decimals
 */
export const maxCalc = (rowData) => {
  let max = -Infinity;
  for (let i = 0; i < rowData.length; i++) {
    max = Math.max(rowData[i], max);
  }
  return (max * 100).toFixed(2);
};

/**
 * Find minimum value and convert to percentage with 2 decimals
 */
export const minCalc = (rowData) => {
  let min = Infinity;
  for (let i = 0; i < rowData.length; i++) {
    min = Math.min(rowData[i], min);
  }
  return (min * 100).toFixed(2);
};

/**
 * Calculate average with 2 decimal places
 */
export const avgCalc = (rowData) => {
  let sum = 0;
  for (let i = 0; i < rowData.length; i++) {
    sum += Number(rowData[i]);
  }
  const avg = sum / rowData.length;
  return avg.toFixed(2);
};

/**
 * Calculate average and convert to percentage with 2 decimals
 */
export const avgCalculate = (rowData) => {
  let sum = 0;
  for (let i = 0; i < rowData.length; i++) {
    sum += rowData[i];
  }
  const avg = sum / rowData.length;
  return (avg * 100).toFixed(2);
};

/**
 * Calculate maximum drawdown
 */
export const maxDrawDown = (wealthArr) => {
  const dd = [];
  for (let i = 1; i <= wealthArr.length; i++) {
    dd.push(wealthArr[i - 1] / Math.max(...wealthArr.slice(0, i)) - 1);
  }
  return (Math.min(...dd) * 100).toFixed(1);
};

/**
 * Shuffle matrix rows using Fisher-Yates algorithm
 */
export const shuffleMatrixRows = (matrix) => {
  return matrix.map((row) => {
    const shuffledRow = [...row];
    for (let i = shuffledRow.length - 1; i > 0; i--) {
      const j = Math.floor(scorecardRandom() * (i + 1));
      [shuffledRow[i], shuffledRow[j]] = [shuffledRow[j], shuffledRow[i]];
    }
    return shuffledRow;
  });
};

/**
 * Fill new matrix with random years from asset bins
 */
export const fillNewMatrix = (binMatrix, assetbinMatrix) => {
  return binMatrix.map((row) =>
    row.map((binIndex) => {
      const availableYears = assetbinMatrix[binIndex];
      return availableYears[Math.floor(scorecardRandom() * availableYears.length)];
    })
  );
};

/**
 * Filter regime years based on available year assets
 */
export const filterRegimeYears = (regimeMatrix, yearAssets) => {
  return regimeMatrix.map((regimeYears) =>
    regimeYears.filter((year) => {
      for (const asset of yearAssets) {
        if (asset[0] === year) {
          return true;
        }
      }
      return false;
    })
  );
};

/**
 * Create bin matrix mapping years to regime indices
 */
export const createBinMatrix = (yearsMatrix, regimeMatrix) => {
  const newMatrix = Array.from({ length: yearsMatrix.length }, () =>
    Array(yearsMatrix[0].length).fill(0)
  );
  for (let i = 0; i < yearsMatrix.length; i++) {
    for (let j = 0; j < yearsMatrix[0].length; j++) {
      const currentYear = yearsMatrix[i][j];
      for (
        let regimeIndex = 0;
        regimeIndex < regimeMatrix.length;
        regimeIndex++
      ) {
        if (regimeMatrix[regimeIndex].includes(currentYear)) {
          newMatrix[i][j] = regimeIndex;
          break;
        }
      }
    }
  }
  return newMatrix;
};

/**
 * Calculate returns for given years
 */
export const calReturn = (resultArray, initialSPXReturn) => {
  let spxRet = [];
  for (let i = 0; i < resultArray.length; i++) {
    let newRow = [];
    for (let j = 0; j < resultArray[i].length; j++) {
      initialSPXReturn.forEach((val) => {
        if (val[0] == resultArray[i][j]) {
          newRow.push(val[1]);
        }
      });
    }
    spxRet.push(newRow);
  }
  return spxRet;
};

/**
 * Calculate year matrix from SPX returns and face matrix
 */
export const calYearMatrix = (initialSPXReturn, matrix) => {
  if (!initialSPXReturn || initialSPXReturn.length === 0) {
    return [];
  }
  let yearMatrixArr = [];
  for (let i = 0; i < matrix.length; i++) {
    let newRow = [];
    for (let j = 0; j < matrix[i].length; j++) {
      const idx = matrix[i][j] - 1;
      if (initialSPXReturn[idx]) {
        newRow.push(initialSPXReturn[idx][0]);
      }
    }
    yearMatrixArr.push(newRow);
  }
  return yearMatrixArr;
};

/**
 * Calculate percentile paths from wealth matrix
 */
export const calculatePercentilePaths = (
  wealthMatrix,
  selectedValueFlip,
  selectedValuePath
) => {
  if (wealthMatrix.length > 1) {
    const lastColumn = wealthMatrix.map((row) => row[selectedValueFlip - 1]);
    const rowsWithWealth = lastColumn.map((wealth, index) => ({
      wealth,
      row: index,
    }));
    rowsWithWealth.sort((a, b) => a.wealth - b.wealth);
    const percentileValues = [];
    const percentiles = [10, 20, 30, 40, 50, 60, 70, 80, 90];
    percentileValues.push(rowsWithWealth[0].row);
    for (let i = 0; i < percentiles.length; i++) {
      const index = Math.floor((percentiles[i] / 100) * selectedValuePath);
      const percentilePath = rowsWithWealth[index - 1].row;
      percentileValues.push(percentilePath);
    }
    percentileValues.push(rowsWithWealth[rowsWithWealth.length - 1].row);
    return percentileValues;
  }
};

/**
 * Calculate wealth matrix for 120-face dice simulation
 */
export const calculateWealthMatrixForOneTwenty = (
  matrix,
  profitLossVal,
  type
) => {
  const wealthMatrix = [];
  const profitLossMatrix = [];

  for (let row = 0; row < matrix.length; row++) {
    const newRow = [];
    const profitLossRow = [];
    let wealth = 1;

    for (let col = 0; col < matrix[row].length; col++) {
      let faceNo = matrix[row][col];
      let faceNoProfitLossVal;
      if (profitLossVal.has(faceNo)) {
        faceNoProfitLossVal = profitLossVal.get(faceNo);
      }
      let profitLossPercentage = faceNoProfitLossVal;
      profitLossRow.push(profitLossPercentage);
      let profitLossPercentageWithoutSign = Math.abs(profitLossPercentage);
      let profitLossCalc = wealth;
      profitLossCalc *= profitLossPercentageWithoutSign;
      if (profitLossPercentage < 0) {
        wealth -= profitLossCalc;
      } else {
        wealth += profitLossCalc;
      }
      newRow.push(wealth);
    }

    wealthMatrix.push(newRow);
    profitLossMatrix.push(profitLossRow);
  }
  return [wealthMatrix, profitLossMatrix];
};

/**
 * Get last column values from wealth matrix
 */
export const lastColumnValue = (
  percentileValuesForLastCol,
  wealthMatrixData
) => {
  const endColumnData = [];
  percentileValuesForLastCol.forEach((rowNo) => {
    if (wealthMatrixData[rowNo]) {
      const rowData = wealthMatrixData[rowNo];
      const endColumnValue = rowData[rowData.length - 1];
      endColumnData.push(endColumnValue.toFixed(2));
    }
  });
  return endColumnData;
};

/**
 * Calculate AM data
 */
export const amDataCal = (percentileValuesForLastCol, profitLossData) => {
  const amDataArr = [];
  percentileValuesForLastCol.forEach((rowNo) => {
    if (profitLossData[rowNo]) {
      const rowData = profitLossData[rowNo];
      const amRowData = avgCal(rowData);
      amDataArr.push(amRowData);
    }
  });
  return amDataArr;
};

/**
 * Calculate drawdown data
 */
export const ddDataCal = (percentileValuesForLastCol, wealthMatrixData) => {
  const endColumnDd = [];
  percentileValuesForLastCol.forEach((rowNo) => {
    if (wealthMatrixData[rowNo]) {
      const rowData = wealthMatrixData[rowNo];
      const endColumnValue = maxDrawDown(rowData);
      endColumnDd.push(endColumnValue);
    }
  });
  return endColumnDd;
};

/**
 * Calculate CAGR
 */
export const cagrCal = (
  percentileValuesForLastCol,
  wealthMatrixData,
  selectedValueFlip
) => {
  const resultArray = [];
  if (percentileValuesForLastCol !== undefined) {
    percentileValuesForLastCol.forEach((rowNo) => {
      if (wealthMatrixData[rowNo]) {
        const rowVal = wealthMatrixData[rowNo];
        const endColValue = rowVal[rowVal.length - 1];
        const modifiedValue = Math.pow(endColValue, 1 / selectedValueFlip) - 1;
        const newModifiedNo = (modifiedValue * 100).toFixed(1);
        resultArray.push(newModifiedNo);
      }
    });
  }
  return resultArray;
};

/**
 * Calculate AM and GM metrics
 */
export const calAmGm = (
  percentileValuesForLastCol,
  selectedValueFlip,
  wealthMatrixData,
  profitLossData
) => {

  let cagrData = cagrCal(
    percentileValuesForLastCol,
    wealthMatrixData,
    selectedValueFlip
  );
  let amData = amDataCal(percentileValuesForLastCol, profitLossData);
  let amDataAvg = avgCalc(amData);
  let gmCal = cagrData[5];
  return { am: amDataAvg, gm: gmCal };
};

/**
 * Calculate reshuffled bin
 */
export const calReshuffledBin = (shuffledMatrix, binMatrix, assetBin) => {
  const newBin = new Array(assetBin.length).fill([]).map(() => []);
  for (let i = 0; i < shuffledMatrix.length; i++) {
    for (let j = 0; j < shuffledMatrix[i].length; j++) {
      let binValue = binMatrix[i][j];
      let yearValue = shuffledMatrix[i][j];
      newBin[binValue].push(yearValue);
    }
  }
  return newBin;
};

/**
 * Generate PNL values map from asset returns
 */
export const generatePNLValues = (assetReturns) => {
  let initialSPXReturn = [...assetReturns].sort((a, b) => a[0] - b[0]);
  let profitLossVal = new Map();
  for (let i = 0; i < initialSPXReturn.length; i++) {
    profitLossVal.set(initialSPXReturn[i][0], initialSPXReturn[i][1]);
  }
  return profitLossVal;
};
